import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Copy } from "lucide-react";
import { ScorePill } from "@/components/NaturalnessScore";

export default function History() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/history"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this rewrite?")) return;
    await api.delete(`/history/${id}`);
    setItems((s) => s.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  const copy = async (text) => {
    await navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <div className="mb-8">
        <div className="label-eyebrow mb-2">History</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Every rewrite, kept.</h1>
      </div>
      {loading ? (
        <div className="text-[hsl(var(--muted-foreground))] text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="surface p-10 text-center text-[hsl(var(--muted-foreground))]">
          No rewrites yet. Head back to the dashboard and humanize something.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((h) => (
            <div key={h.id} data-testid={`history-item-${h.id}`} className="surface p-5">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex flex-wrap items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                  <span>{new Date(h.created_at).toLocaleString()}</span>
                  <span>·</span>
                  <span className="pill py-0.5 text-[10px]">{h.tone}</span>
                  <span className="pill py-0.5 text-[10px]">{h.audience}</span>
                  <span className="pill py-0.5 text-[10px]">{h.strength}</span>
                  <span>·</span>
                  <span>{h.words} words</span>
                  {h.naturalness?.score != null && (
                    <>
                      <span>·</span>
                      <ScorePill score={h.naturalness.score} testId={`score-pill-${h.id}`} />
                    </>
                  )}
                </div>
                <div className="flex gap-2">
                  <button data-testid={`copy-history-${h.id}`} onClick={() => copy(h.humanized)} className="pill hover:bg-[hsl(var(--secondary))]">
                    <Copy size={12} /> Copy
                  </button>
                  <button data-testid={`delete-history-${h.id}`} onClick={() => del(h.id)} className="pill hover:bg-red-50 hover:text-red-700 hover:border-red-200">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setOpenId(openId === h.id ? null : h.id)}
                  className="text-sm text-[hsl(var(--primary))] link-underline font-semibold"
                >
                  {openId === h.id ? "Hide comparison" : "Show comparison"}
                </button>
              </div>
              {openId === h.id && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="label-eyebrow mb-2">Original</div>
                    <div className="bg-[hsl(var(--muted))] rounded-xl p-4 text-sm whitespace-pre-wrap max-h-72 overflow-auto">{h.original}</div>
                  </div>
                  <div>
                    <div className="label-eyebrow mb-2">Humanized</div>
                    <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-4 text-sm whitespace-pre-wrap max-h-72 overflow-auto">{h.humanized}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
